import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMap, MapEventManager, AriaLabelFn, Calculator, MapMarker, MarkerClustererOptions } from '@angular/google-maps';

import { Subject, Observable, take, takeUntil } from 'rxjs';
import { LegColorOptions, OverlappingMarkerSpiderfier, SpiderOptions } from 'i-hate-spiders';
import { CustomMapMarkerDirective } from '../custom-map-marker/custom-map-marker.component';

@Component({
  selector: 'map-marker-spiderifier',
  exportAs: 'mapMarkerSpiderifier',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<ng-content/>',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class MapMarkerSpiderifierComponent implements OnInit, AfterContentInit, OnChanges, OnDestroy {
  private readonly _googleMap = inject(GoogleMap);
  private readonly _ngZone = inject(NgZone);
  private readonly _currentMarkers = new Set<google.maps.Marker>();
  private readonly _eventManager = new MapEventManager(inject(NgZone));
  private readonly _destroy = new Subject<void>();

  /** Whether the clusterer is allowed to be initialized. */
  private readonly _canInitialize = this._googleMap._isBrowser;

  @Input()
  set options(options: SpiderOptions) {
    this._options = options;
  }
  private _options: SpiderOptions = {
    markersWontMove: false,
    markersWontHide: false,
    basicFormatEvents: false,
    keepSpiderfied: false,
    legWeight: 15,
  };

  @Input() clusterMarkers: boolean = false;

  @Input()
  set clusterOptions(options: MarkerClustererOptions) {
    this._clusterOptions = options;
  }
  private _clusterOptions: MarkerClustererOptions = {
    imagePath: '/assets/gfx/map/cluster-size-',
    imageExtension: 'png',
    minimumClusterSize: 4,
    maxZoom: 18,
  };

  @Input()
  set legColors(colors: LegColorOptions) {
    this._legColors.highlighted = {
      ...this._legColors.highlighted,
      ...colors.highlighted,
    };
    this._legColors.usual = {
      ...this._legColors.usual,
      ...colors.usual,
    };
  }

  private _legColors: LegColorOptions = {
    highlighted: {
      [google.maps.MapTypeId.HYBRID]: '#f00',
      [google.maps.MapTypeId.ROADMAP]: '#f00',
      [google.maps.MapTypeId.SATELLITE]: '#f00',
      [google.maps.MapTypeId.TERRAIN]: '#f00',
    },
    usual: {
      [google.maps.MapTypeId.HYBRID]: '#fff',
      [google.maps.MapTypeId.ROADMAP]: '#444',
      [google.maps.MapTypeId.SATELLITE]: '#fff',
      [google.maps.MapTypeId.TERRAIN]: '#444',
    },
  };

  @ContentChildren(CustomMapMarkerDirective, { descendants: true }) _markers: QueryList<CustomMapMarkerDirective> = new QueryList();

  /**
   * The underlying markerSpiderifier object.
   *
   * See
   * googlemaps.github.io/v3-utility-library/classes/
   * _google_markerSpiderifierplus.markerSpiderifier.html
   */
  markerSpiderifier?: OverlappingMarkerSpiderfier;

  /**
   * The underlying MarkerClusterer object.
   *
   * See
   * googlemaps.github.io/v3-utility-library/classes/
   * _google_markerclustererplus.markerclusterer.html
   */
  markerClusterer?: any;

  /** Event emitted when the clusterer is initialized. */
  @Output() readonly markerSpiderifierInitialized: EventEmitter<OverlappingMarkerSpiderfier> =
    new EventEmitter<OverlappingMarkerSpiderfier>();

  /** Event emitted when the clusterer is initialized. */
  /** Emits when a cluster has been clicked. */
  @Output()
  readonly spiderClick: Observable<MapMarker> = this._eventManager.getLazyEmitter<MapMarker>('spider_click');

  constructor(...args: unknown[]);
  constructor() {}

  ngOnInit() {
    if (this._canInitialize) {
      if (typeof OverlappingMarkerSpiderfier !== 'function') {
        throw Error(
          'OverlappingMarkerSpiderfier class not found, cannot construct a marker cluster. ' +
            'Please install the I-Hate-Spiders library: ' +
            'https://www.npmjs.com/package/i-hate-spiders'
        );
      }

      // Create the object outside the zone so its events don't trigger change detection.
      // We'll bring it back in inside the `MapEventManager` only for the events that the
      // user has subscribed to.
      this._ngZone.runOutsideAngular(() => {
        if (this.clusterMarkers) {
          this.markerClusterer = new (<any>window).MarkerClusterer(this._googleMap.googleMap!, [], this._clusterOptions);
        }
        this.markerSpiderifier = new OverlappingMarkerSpiderfier(this._googleMap.googleMap!, this._options);
        this.markerSpiderifier.legColors = this._legColors;
      });

      this._assertInitialized();
      this._eventManager.setTarget(this.markerSpiderifier as any);
    }
  }

  ngAfterContentInit() {
    if (this._canInitialize) {
      if (this.markerSpiderifier) {
        this._watchForMarkerChanges();
      } else {
        this.markerSpiderifierInitialized.pipe(take(1), takeUntil(this._destroy)).subscribe(() => this._watchForMarkerChanges());
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const { markerSpiderifier: spiderifier } = this;

    if (spiderifier) {
      if (changes['options']) {
        spiderifier.keepSpiderfied = this.options.keepSpiderfied ?? spiderifier.keepSpiderfied;
        spiderifier.markersWontHide = this.options.markersWontHide ?? spiderifier.markersWontHide;
        spiderifier.markersWontMove = this.options.markersWontMove ?? spiderifier.markersWontMove;
        spiderifier.basicFormatEvents = this.options.basicFormatEvents ?? spiderifier.basicFormatEvents;
      }
      if (changes['legColors']) {
        spiderifier.legColors = this._legColors;
      }
    }
  }

  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();
    this._eventManager.destroy();
    // this.markerSpiderifier.setMap(null);
  }

  getTotalMarkers(): number {
    this._assertInitialized();
    return this.markerSpiderifier.getMarkers()?.length ?? 0;
  }

  private _watchForMarkerChanges() {
    this._assertInitialized();
    const initialMarkers: google.maps.Marker[] = [];
    for (const marker of this._getInternalMarkers(this._markers.toArray())) {
      this._currentMarkers.add(marker);
      initialMarkers.push(marker);
    }
    for (const marker of initialMarkers) {
      this.markerSpiderifier.addMarker(marker as any, () => {});
    }

    if (this.clusterMarkers) {
      this.markerClusterer.addMarkers(initialMarkers);
    }

    this._markers.changes.pipe(takeUntil(this._destroy)).subscribe((markerComponents: MapMarker[]) => {
      this._assertInitialized();
      const newMarkers = new Set<google.maps.Marker>(this._getInternalMarkers(markerComponents));
      const markersToAdd: google.maps.Marker[] = [];
      const markersToRemove: google.maps.Marker[] = [];
      for (const marker of Array.from(newMarkers)) {
        if (!this._currentMarkers.has(marker)) {
          this._currentMarkers.add(marker);
          markersToAdd.push(marker);
        }
      }
      for (const marker of Array.from(this._currentMarkers)) {
        if (!newMarkers.has(marker)) {
          markersToRemove.push(marker);
        }
      }

      for (const marker of markersToAdd) {
        this.markerSpiderifier.addMarker(marker, () => {});
      }
      for (const marker of markersToRemove) {
        this.markerSpiderifier.removeMarker(marker);
      }
      for (const marker of markersToRemove) {
        this._currentMarkers.delete(marker);
      }

      if (this.clusterMarkers) {
        this.markerClusterer.addMarkers(markersToAdd, true);
        this.markerClusterer.removeMarkers(markersToRemove, true);
        this.markerClusterer.repaint();
      }
    });
  }

  private _getInternalMarkers(markers: MapMarker[]): google.maps.Marker[] {
    return markers.filter(markerComponent => !!markerComponent.marker).map(markerComponent => markerComponent.marker!);
  }

  private _assertInitialized(): asserts this is { markerSpiderifier: OverlappingMarkerSpiderfier } {
    if (!this._googleMap.googleMap) {
      throw Error(
        'Cannot access Google Map information before the API has been initialized. ' +
          'Please wait for the API to load before trying to interact with it.'
      );
    }
    if (!this.markerSpiderifier) {
      throw Error(
        'Cannot interact with a OverlappingMarkerSpiderfier before it has been initialized. ' +
          'Please wait for the OverlappingMarkerSpiderfier to load before trying to interact with it.'
      );
    }
  }
}
